// Environment configuration and validation for GovSim Frontend

interface EnvironmentConfig {
  API_URL: string;
  AI_FEATURES_ENABLED: boolean;
  WEBSOCKET_URL: string;
  ENVIRONMENT: 'development' | 'production' | 'test';
  
  // Feature flags
  CHARACTER_DRAMA_ENABLED: boolean;
  MEDIA_ECOSYSTEM_ENABLED: boolean;
  INTELLIGENCE_SYSTEM_ENABLED: boolean;
  LIVING_WORLD_ENABLED: boolean;
  REAL_TIME_UPDATES_ENABLED: boolean;
  
  // Debug and monitoring
  DEBUG_MODE: boolean;
  ANALYTICS_ENABLED: boolean;
  PERFORMANCE_MONITORING: boolean;
}

class Environment {
  private config: EnvironmentConfig;

  constructor() {
    this.config = {
      API_URL: import.meta.env.VITE_API_URL || 'http://localhost:8787',
      AI_FEATURES_ENABLED: import.meta.env.VITE_AI_FEATURES_ENABLED === 'true',
      WEBSOCKET_URL: import.meta.env.VITE_WEBSOCKET_URL || 'ws://localhost:8787',
      ENVIRONMENT: (import.meta.env.VITE_ENVIRONMENT as 'development' | 'production' | 'test') || 'development',
      
      // Feature flags
      CHARACTER_DRAMA_ENABLED: import.meta.env.VITE_CHARACTER_DRAMA_ENABLED === 'true',
      MEDIA_ECOSYSTEM_ENABLED: import.meta.env.VITE_MEDIA_ECOSYSTEM_ENABLED === 'true',
      INTELLIGENCE_SYSTEM_ENABLED: import.meta.env.VITE_INTELLIGENCE_SYSTEM_ENABLED === 'true',
      LIVING_WORLD_ENABLED: import.meta.env.VITE_LIVING_WORLD_ENABLED === 'true',
      REAL_TIME_UPDATES_ENABLED: import.meta.env.VITE_REAL_TIME_UPDATES_ENABLED === 'true',
      
      // Debug and monitoring
      DEBUG_MODE: import.meta.env.VITE_DEBUG_MODE === 'true',
      ANALYTICS_ENABLED: import.meta.env.VITE_ANALYTICS_ENABLED === 'true',
      PERFORMANCE_MONITORING: import.meta.env.VITE_PERFORMANCE_MONITORING === 'true',
    };

    this.validateConfiguration();
  }

  /**
   * Get the current environment configuration
   */
  getConfig(): EnvironmentConfig {
    return { ...this.config };
  }

  /**
   * Get a specific configuration value
   */
  get<K extends keyof EnvironmentConfig>(key: K): EnvironmentConfig[K] {
    return this.config[key];
  }

  /**
   * Check if a feature is enabled
   */
  isFeatureEnabled(feature: keyof Pick<EnvironmentConfig, 
    'AI_FEATURES_ENABLED' | 
    'CHARACTER_DRAMA_ENABLED' | 
    'MEDIA_ECOSYSTEM_ENABLED' | 
    'INTELLIGENCE_SYSTEM_ENABLED' | 
    'LIVING_WORLD_ENABLED' | 
    'REAL_TIME_UPDATES_ENABLED' |
    'DEBUG_MODE' |
    'ANALYTICS_ENABLED' |
    'PERFORMANCE_MONITORING'
  >): boolean {
    return this.config[feature];
  }

  /**
   * Check if running in development mode
   */
  isDevelopment(): boolean {
    return this.config.ENVIRONMENT === 'development';
  }

  /**
   * Check if running in production mode
   */
  isProduction(): boolean {
    return this.config.ENVIRONMENT === 'production';
  }

  /**
   * Get API base URL with proper formatting
   */
  getApiUrl(endpoint?: string): string {
    const baseUrl = this.config.API_URL.replace(/\/$/, ''); // Remove trailing slash
    return endpoint ? `${baseUrl}${endpoint.startsWith('/') ? endpoint : '/' + endpoint}` : baseUrl;
  }

  /**
   * Get WebSocket URL with proper formatting
   */
  getWebSocketUrl(): string {
    return this.config.WEBSOCKET_URL;
  }

  /**
   * Validate that all required environment variables are set
   */
  private validateConfiguration(): void {
    const errors: string[] = [];

    // Required variables
    if (!this.config.API_URL) {
      errors.push('VITE_API_URL is required');
    }

    if (!this.isValidUrl(this.config.API_URL)) {
      errors.push('VITE_API_URL must be a valid URL');
    }

    if (this.config.WEBSOCKET_URL && !this.isValidWebSocketUrl(this.config.WEBSOCKET_URL)) {
      errors.push('VITE_WEBSOCKET_URL must be a valid WebSocket URL');
    }

    // Environment validation
    if (!['development', 'production', 'test'].includes(this.config.ENVIRONMENT)) {
      errors.push('VITE_ENVIRONMENT must be "development", "production", or "test"');
    }

    // Production-specific validations
    if (this.isProduction()) {
      if (this.config.API_URL.includes('localhost')) {
        console.warn('⚠️  Production build is using localhost API URL');
      }

      if (this.config.DEBUG_MODE) {
        console.warn('⚠️  Debug mode is enabled in production');
      }
    }

    // Log errors
    if (errors.length > 0) {
      console.error('❌ Environment Configuration Errors:');
      errors.forEach(error => console.error(`  - ${error}`));
      
      if (this.isProduction()) {
        throw new Error('Invalid environment configuration for production');
      }
    } else {
      console.log('✅ Environment configuration validated successfully');
      
      if (this.isDevelopment()) {
        console.log('📋 Environment Summary:', {
          API_URL: this.config.API_URL,
          ENVIRONMENT: this.config.ENVIRONMENT,
          AI_FEATURES: this.config.AI_FEATURES_ENABLED ? '✅' : '❌',
          ADVANCED_FEATURES: {
            CHARACTER_DRAMA: this.config.CHARACTER_DRAMA_ENABLED ? '✅' : '❌',
            MEDIA_ECOSYSTEM: this.config.MEDIA_ECOSYSTEM_ENABLED ? '✅' : '❌',
            INTELLIGENCE: this.config.INTELLIGENCE_SYSTEM_ENABLED ? '✅' : '❌',
            LIVING_WORLD: this.config.LIVING_WORLD_ENABLED ? '✅' : '❌',
            REAL_TIME: this.config.REAL_TIME_UPDATES_ENABLED ? '✅' : '❌',
          }
        });
      }
    }
  }

  /**
   * Validate URL format
   */
  private isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Validate WebSocket URL format
   */
  private isValidWebSocketUrl(url: string): boolean {
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'ws:' || parsed.protocol === 'wss:';
    } catch {
      return false;
    }
  }

  /**
   * Get configuration for debugging
   */
  getDebugInfo(): object {
    if (!this.isDevelopment()) {
      return { message: 'Debug info only available in development mode' };
    }

    return {
      environment: this.config.ENVIRONMENT,
      apiUrl: this.config.API_URL,
      websocketUrl: this.config.WEBSOCKET_URL,
      features: {
        ai: this.config.AI_FEATURES_ENABLED,
        characterDrama: this.config.CHARACTER_DRAMA_ENABLED,
        mediaEcosystem: this.config.MEDIA_ECOSYSTEM_ENABLED,
        intelligence: this.config.INTELLIGENCE_SYSTEM_ENABLED,
        livingWorld: this.config.LIVING_WORLD_ENABLED,
        realTime: this.config.REAL_TIME_UPDATES_ENABLED,
      },
      debug: {
        debugMode: this.config.DEBUG_MODE,
        analytics: this.config.ANALYTICS_ENABLED,
        performance: this.config.PERFORMANCE_MONITORING,
      }
    };
  }
}

// Create singleton instance
export const environment = new Environment();

// Export configuration object for compatibility
export const config = environment.getConfig();

// Export utility functions
export const isFeatureEnabled = (feature: Parameters<typeof environment.isFeatureEnabled>[0]) => 
  environment.isFeatureEnabled(feature);

export const isDevelopment = () => environment.isDevelopment();
export const isProduction = () => environment.isProduction();
export const getApiUrl = (endpoint?: string) => environment.getApiUrl(endpoint);
export const getWebSocketUrl = () => environment.getWebSocketUrl();

// Export types for TypeScript users
export type { EnvironmentConfig };

export default environment;